// What each side-menu item counts: only what waits on the operator. A count of zero shows no
// badge, so the menu never shows 0; loading and failed reads are the caller's to drop.
import { Option } from 'effect';
import type {
  DlqInboxPage,
  OnboardingOverview,
  RegulatoryEndpointSnapshot,
} from '@finnest/power-admin-contracts';
import { statusCounts } from '../../regulatory-drift/model/drift-view.ts';

/** The badge beside a menu item: the visible text, its tone and what it says in full. */
export type NavBadge = {
  readonly text: string;
  readonly tone: 'warning' | 'danger';
  readonly description: string;
};

/** The badge for `count`, captioned `text`; none when nothing waits on the operator. */
const badgeOf = (
  count: number,
  tone: NavBadge['tone'],
  describe: (count: number) => string,
  text = String(count),
): Option.Option<NavBadge> =>
  count === 0 ? Option.none() : Option.some({ text, tone, description: describe(count) });

/**
 * The open phases with at least one sub-step waiting on the operator. A phase locked behind its
 * prerequisites keeps its sub-steps' statuses, but its screen offers no action until it opens.
 */
export const onboardingBadge = (overview: OnboardingOverview): Option.Option<NavBadge> =>
  badgeOf(
    overview.phases.filter(
      (phase) =>
        phase.status.kind !== 'locked' &&
        phase.subSteps.some((subStep) => subStep.status.kind === 'action-pending'),
    ).length,
    'warning',
    (count) => (count === 1 ? '1 fase pede ação' : `${String(count)} fases pedem ação`),
  );

/** The endpoints in drift or missing across `snapshots`, one per served ecosystem. */
export const driftBadge = (
  snapshots: readonly RegulatoryEndpointSnapshot[],
): Option.Option<NavBadge> =>
  badgeOf(
    snapshots
      .map((snapshot) => statusCounts(snapshot.rows))
      .reduce((sum, counts) => sum + counts.drift + counts.missing, 0),
    'warning',
    (count) =>
      count === 1 ? '1 endpoint com divergência' : `${String(count)} endpoints com divergência`,
  );

/** The receiver connections in error. */
export const receiverBadge = (errorCount: number): Option.Option<NavBadge> =>
  badgeOf(errorCount, 'danger', (count) =>
    count === 1 ? '1 conexão com falha' : `${String(count)} conexões com falha`,
  );

/** The parked events on the DLQ's newest page; more follow when the page has a next one. */
export const dlqBadge = (page: DlqInboxPage): Option.Option<NavBadge> =>
  Option.isSome(page.next)
    ? badgeOf(
        page.items.length,
        'danger',
        (count) => `mais de ${String(count)} eventos na fila de mensagens com falha`,
        `${String(page.items.length)}+`,
      )
    : badgeOf(page.items.length, 'danger', (count) =>
        count === 1
          ? '1 evento na fila de mensagens com falha'
          : `${String(count)} eventos na fila de mensagens com falha`,
      );
